import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { User } from 'firebase/auth';
import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { Member, MemberRole, MEMBER_ROLE_OPTIONS } from '../types/app';
import { validateImageFile } from '../utils/imageValidation';
import { compressImage } from '../utils/compressImage';

export function useMembers(currentUser: User | null, hasAppAccess: boolean, isAdmin: boolean) {
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [memberSuccess, setMemberSuccess] = useState('');
  const [memberName, setMemberName] = useState('');
  const [memberRole, setMemberRole] = useState<MemberRole>('ovrige');
  const [memberDescription, setMemberDescription] = useState('');
  const [memberImage, setMemberImage] = useState<File | null>(null);
  const [isSubmittingMember, setIsSubmittingMember] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<MemberRole>('ovrige');
  const [editDescription, setEditDescription] = useState('');
  const [editImage, setEditImage] = useState<File | null>(null);
  const [isUpdatingMember, setIsUpdatingMember] = useState(false);
  const [isDeletingMember, setIsDeletingMember] = useState(false);

  const getRoleLabel = (role: MemberRole): string =>
    MEMBER_ROLE_OPTIONS.find((option) => option.value === role)?.label || 'Øvrige medlemmer';

  const memberNameById = useMemo(
    () => new Map(members.map((member) => [member.id, member.navn] as const)),
    [members]
  );

  const memberById = useMemo(
    () => new Map(members.map((member) => [member.id, member] as const)),
    [members]
  );

  const memberAvatarById = useMemo(() => {
    const avatarById = new Map<string, HTMLImageElement>();

    members.forEach((member) => {
      if (!member.billedeUrl) {
        return;
      }

      const avatarImage = new Image();
      avatarImage.src = member.billedeUrl;
      avatarById.set(member.id, avatarImage);
    });

    return avatarById;
  }, [members]);

  const fetchMembers = useCallback(async () => {
    if (!currentUser || !hasAppAccess) {
      return;
    }

    setMembersError('');
    setIsLoadingMembers(true);
    try {
      const membersQuery = query(collection(db, 'members'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(membersQuery);
      const mappedMembers: Member[] = snapshot.docs.map((docItem) => {
        const data = docItem.data() as {
          navn?: string;
          rolle?: string;
          beskrivelse?: string;
          billedeUrl?: string;
          oprettetAf?: string;
          createdAt?: { toDate: () => Date };
        };

        const roleFromData = data.rolle;
        const validatedRole: MemberRole =
          roleFromData === 'formand' || roleFromData === 'kasserer' || roleFromData === 'ovrige'
            ? roleFromData
            : 'ovrige';

        return {
          id: docItem.id,
          navn: data.navn ?? '',
          rolle: validatedRole,
          beskrivelse: data.beskrivelse ?? '',
          billedeUrl: data.billedeUrl ?? '',
          billedePath: (data as { billedePath?: string }).billedePath,
          oprettetAf: data.oprettetAf ?? '',
          oprettetAt: data.createdAt ? data.createdAt.toDate().toISOString() : null,
        };
      });
      setMembers(mappedMembers);
    } catch (error: unknown) {
      setMembersError('Kunne ikke hente medlemmer.');
    } finally {
      setIsLoadingMembers(false);
    }
  }, [currentUser, hasAppAccess]);

  useEffect(() => {
    if (!currentUser || !hasAppAccess) {
      setMembers([]);
      return;
    }

    void fetchMembers();
  }, [currentUser, hasAppAccess, fetchMembers]);

  const handleCreateMember = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!currentUser || !hasAppAccess) {
      setMembersError('Du har ikke adgang til at oprette medlemmer.');
      return;
    }

    setMembersError('');
    setMemberSuccess('');

    if (!memberName.trim()) {
      setMembersError('Navn er paakraevet.');
      return;
    }

    if (!memberDescription.trim()) {
      setMembersError('Beskrivelse er paakraevet.');
      return;
    }

    if (!memberImage) {
      setMembersError('Du skal vaelge et billede.');
      return;
    }

    const fileError = validateImageFile(memberImage);
    if (fileError) {
      setMembersError(fileError);
      return;
    }

    setIsSubmittingMember(true);
    try {
      const imageToUpload = await compressImage(memberImage);
      const sanitizedName = imageToUpload.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const imagePath = `member-images/${currentUser.uid}/${Date.now()}-${sanitizedName}`;
      const imageRef = ref(storage, imagePath);

      await uploadBytes(imageRef, imageToUpload, {
        contentType: imageToUpload.type,
      });

      const imageUrl = await getDownloadURL(imageRef);

      await addDoc(collection(db, 'members'), {
        navn: memberName.trim(),
        rolle: memberRole,
        beskrivelse: memberDescription.trim(),
        billedeUrl: imageUrl,
        billedePath: imagePath,
        oprettetAf: currentUser.uid,
        createdAt: serverTimestamp(),
      });

      setMemberName('');
      setMemberRole('ovrige');
      setMemberDescription('');
      setMemberImage(null);
      setMemberSuccess('Medlem er oprettet.');
      await fetchMembers();
    } catch (error: unknown) {
      setMembersError('Kunne ikke oprette medlem.');
    } finally {
      setIsSubmittingMember(false);
    }
  };

  const startEditMember = (member: Member) => {
    setMembersError('');
    setMemberSuccess('');
    setEditingMemberId(member.id);
    setEditName(member.navn);
    setEditRole(member.rolle);
    setEditDescription(member.beskrivelse);
    setEditImage(null);
  };

  const cancelEditMember = () => {
    setEditingMemberId(null);
    setEditName('');
    setEditRole('ovrige');
    setEditDescription('');
    setEditImage(null);
  };

  const handleUpdateMember = async (member: Member) => {
    if (!currentUser || !hasAppAccess) {
      setMembersError('Du har ikke adgang til at redigere medlemmer.');
      return;
    }

    if (!editName.trim()) {
      setMembersError('Navn er paakraevet.');
      return;
    }

    if (!editDescription.trim()) {
      setMembersError('Beskrivelse er paakraevet.');
      return;
    }

    setMembersError('');
    setMemberSuccess('');
    setIsUpdatingMember(true);

    try {
      let billedeUrl = member.billedeUrl;
      let billedePath = member.billedePath;

      if (editImage) {
        const fileError = validateImageFile(editImage);
        if (fileError) {
          setMembersError(fileError);
          setIsUpdatingMember(false);
          return;
        }

        const imageToUpload = await compressImage(editImage);
        const sanitizedName = imageToUpload.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const newPath = `member-images/${currentUser.uid}/${Date.now()}-${sanitizedName}`;
        const newRef = ref(storage, newPath);

        await uploadBytes(newRef, imageToUpload, {contentType: imageToUpload.type});
        billedeUrl = await getDownloadURL(newRef);
        billedePath = newPath;

        if (member.billedePath) {
          try {
            await deleteObject(ref(storage, member.billedePath));
          } catch (deleteError: unknown) {
            // Keep flow resilient if old file no longer exists.
          }
        }
      }

      await updateDoc(doc(db, 'members', member.id), {
        navn: editName.trim(),
        rolle: editRole,
        beskrivelse: editDescription.trim(),
        billedeUrl,
        billedePath: billedePath ?? '',
      });

      setMemberSuccess('Medlem er opdateret.');
      cancelEditMember();
      await fetchMembers();
    } catch (error: unknown) {
      setMembersError('Kunne ikke opdatere medlem.');
    } finally {
      setIsUpdatingMember(false);
    }
  };

  const handleDeleteMember = async (member: Member) => {
    if (!currentUser || !hasAppAccess) {
      setMembersError('Du har ikke adgang til at slette medlemmer.');
      return;
    }

    const confirmed = window.confirm(`Slet medlemmet ${member.navn}?`);
    if (!confirmed) {
      return;
    }

    setMembersError('');
    setMemberSuccess('');
    setIsDeletingMember(true);

    try {
      await deleteDoc(doc(db, 'members', member.id));

      if (member.billedePath) {
        try {
          await deleteObject(ref(storage, member.billedePath));
        } catch (deleteError: unknown) {
          // Keep deletion successful even if image was already removed.
        }
      }

      setMemberSuccess('Medlem er slettet.');
      if (editingMemberId === member.id) {
        cancelEditMember();
      }
      await fetchMembers();
    } catch (error: unknown) {
      setMembersError('Kunne ikke slette medlem.');
    } finally {
      setIsDeletingMember(false);
    }
  };

  return {
    members,
    isLoadingMembers,
    membersError,
    setMembersError,
    memberSuccess,
    memberName,
    setMemberName,
    memberRole,
    setMemberRole,
    memberDescription,
    setMemberDescription,
    memberImage,
    setMemberImage,
    isSubmittingMember,
    editingMemberId,
    editName,
    setEditName,
    editRole,
    setEditRole,
    editDescription,
    setEditDescription,
    editImage,
    setEditImage,
    isUpdatingMember,
    isDeletingMember,
    getRoleLabel,
    memberNameById,
    memberById,
    memberAvatarById,
    handleCreateMember,
    startEditMember,
    cancelEditMember,
    handleUpdateMember,
    handleDeleteMember,
  };
}
